import { Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Separator } from '@/app/components/ui/separator';
import { TabsContent } from '@/app/components/ui/tabs';
import type { EditorReady } from './types';

export function CampaignTab({ ed }: { ed: EditorReady }) {
  const {
    templateSet, availableFields, selectedFields, sampleDataRow, splitStructureStats,
    updateTemplateContent, getPreviewText, inputProps, insertPlaceholder,
  } = ed;

  return (
    <TabsContent value="campaign" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Field Selection</CardTitle>
          <CardDescription>Select which fields will be available as placeholders throughout the templates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label>Available Fields</Label>
            <div className="flex flex-wrap gap-2">
              {availableFields.map((field) => {
                const isSelected = (templateSet.template_content.selected_fields || []).includes(field);
                return (
                  <Button
                    key={field}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = templateSet.template_content.selected_fields || [];
                      updateTemplateContent('selected_fields', isSelected ? current.filter(f => f !== field) : [...current, field]);
                    }}
                  >
                    {field}
                  </Button>
                );
              })}
            </div>
            <p className="text-sm text-muted-foreground">Selected fields are available as quick-insert tokens in all template tabs.</p>
          </div>
          {sampleDataRow && (
            <div className="space-y-2 pt-4 border-t">
              <Label>Sample Values (first row)</Label>
              <div className="bg-muted p-3 rounded-md space-y-1 max-h-48 overflow-y-auto">
                {(templateSet.template_content.selected_fields || []).map((field) => {
                  const value = field.startsWith('custom:')
                    ? sampleDataRow.custom_fields?.[field.replace('custom:', '')]
                    : (sampleDataRow as any)[field];
                  return (
                    <div key={field} className="text-sm font-mono flex gap-2">
                      <span className="text-muted-foreground">{field}:</span>
                      <span className="font-semibold">{value || '(empty)'}</span>
                    </div>
                  );
                })}
                {(templateSet.template_content.selected_fields || []).length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No fields selected yet</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template-Scoped Placeholders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Placeholders</CardTitle>
              <CardDescription>Fixed values applied to all rows — use these for values that change per template, not per row</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const current = templateSet.template_content.template_placeholders || {};
                const key = `placeholder_${Object.keys(current).length + 1}`;
                updateTemplateContent('template_placeholders', { ...current, [key]: '' });
              }}
            >
              <Plus className="size-3.5 mr-1.5" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const placeholders = templateSet.template_content.template_placeholders || {};
            const entries = Object.entries(placeholders);
            if (entries.length === 0) {
              return (
                <div className="text-center py-6 border border-dashed border-border rounded-lg">
                  <p className="text-sm text-muted-foreground">No template placeholders defined</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Add placeholders like <code className="font-mono text-primary">{'{home_type}'}</code> that resolve to a fixed value for this template
                  </p>
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {entries.map(([key, value], idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-muted-foreground/50 font-mono text-sm shrink-0">{'{'}</span>
                    <Input
                      value={key}
                      onChange={e => {
                        const newKey = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                        if (newKey === key || !newKey) return;
                        const rebuilt: Record<string, string> = {};
                        for (const [k, v] of entries) {
                          rebuilt[k === key ? newKey : k] = v;
                        }
                        updateTemplateContent('template_placeholders', rebuilt);
                      }}
                      placeholder="key"
                      className="h-8 text-sm font-mono w-36 shrink-0"
                    />
                    <span className="text-muted-foreground/50 font-mono text-sm shrink-0">{'}'}</span>
                    <span className="text-muted-foreground/30 shrink-0">=</span>
                    <Input
                      value={value}
                      onChange={e => {
                        updateTemplateContent('template_placeholders', { ...placeholders, [key]: e.target.value });
                      }}
                      placeholder="value for this template"
                      className="h-8 text-sm flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-muted-foreground/40 hover:text-destructive"
                      onClick={() => {
                        const current = { ...placeholders };
                        delete current[key];
                        updateTemplateContent('template_placeholders', Object.keys(current).length > 0 ? current : undefined);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}
                {sampleDataRow && entries.some(([, v]) => v.trim()) && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-muted-foreground/50 mb-1.5 uppercase tracking-wider font-semibold">Preview</p>
                    <div className="bg-muted p-3 rounded-md space-y-1">
                      {entries.filter(([, v]) => v.trim()).map(([k, v]) => (
                        <div key={k} className="text-sm font-mono flex gap-2">
                          <span className="text-muted-foreground">{`{${k}}`}</span>
                          <span className="text-primary font-semibold">→ {v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Campaign Structure</CardTitle>
          <CardDescription>Configure how campaigns and ad groups are organized</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Primary Grouping Field</Label>
            <Select
              value={templateSet.template_content.primary_grouping_field || templateSet.template_content.campaign_grouping_field}
              onValueChange={(value) => {
                updateTemplateContent('primary_grouping_field', value);
                updateTemplateContent('campaign_grouping_field', value);
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Grouping (One Campaign)</SelectItem>
                {availableFields.filter(f => !f.startsWith('custom:')).map((field) => (
                  <SelectItem key={field} value={field}>{field}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">Cities will be grouped into campaigns based on this field</p>
          </div>

          <div className="space-y-2">
            <Label>Campaign Split Field <span className="text-muted-foreground/60">(optional)</span></Label>
            <Select
              value={templateSet.template_content.secondary_grouping_field || '__none__'}
              onValueChange={(value) => updateTemplateContent('secondary_grouping_field', value === '__none__' ? undefined : value)}
            >
              <SelectTrigger><SelectValue placeholder="No split" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No Split</SelectItem>
                {availableFields.map((field) => (
                  <SelectItem key={field} value={field}>{field}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">Divides each primary group into separate campaigns per unique value of this field.</p>
            {splitStructureStats && (
              <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-md border border-blue-200 dark:border-blue-800 space-y-1">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">Split Preview</p>
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <span className="font-bold">{splitStructureStats.primaryCount}</span>
                  <span className="text-blue-600 dark:text-blue-400"> {splitStructureStats.primaryLabel}</span>
                  {' × '}
                  <span className="font-bold">{splitStructureStats.splitCount}</span>
                  <span className="text-blue-600 dark:text-blue-400"> {splitStructureStats.splitLabel}</span>
                  {' = '}
                  <span className="font-bold">{splitStructureStats.campaignCount} campaigns</span>
                </p>
              </div>
            )}
            {splitStructureStats && templateSet.template_content.campaign_name_template && !templateSet.template_content.campaign_name_template.includes(splitStructureStats.splitPlaceholder) && (
              <Alert className="border-amber-500/40 bg-amber-500/5">
                <AlertDescription className="text-xs text-amber-700 dark:text-amber-400">
                  Campaign name template doesn't include <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">{splitStructureStats.splitPlaceholder}</code> — split campaigns will have identical names.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Campaign Name Template</Label>
            <Input
              value={templateSet.template_content.campaign_name_template}
              onChange={(e) => updateTemplateContent('campaign_name_template', e.target.value)}
              placeholder="e.g., Campaign - {region_nom}"
              {...inputProps('campaign_name_template')}
            />
            {templateSet.template_content.campaign_name_template && sampleDataRow && (
              <div className="bg-green-50 dark:bg-green-950/30 p-2.5 rounded-md border-l-4 border-green-500 text-sm">
                <span className="text-green-700 dark:text-green-400 text-xs font-semibold uppercase tracking-wide">Preview: </span>
                <span className="text-green-900 dark:text-green-100 font-bold">{getPreviewText(templateSet.template_content.campaign_name_template)}</span>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {selectedFields.map((ph) => (
                <Button key={ph} variant="outline" size="sm"
                  onClick={() => updateTemplateContent('campaign_name_template', insertPlaceholder(templateSet.template_content.campaign_name_template, ph))}>
                  +{ph}
                </Button>
              ))}
              {splitStructureStats && !selectedFields.includes(templateSet.template_content.secondary_grouping_field!) && (
                <Button variant="outline" size="sm"
                  className="border-blue-400 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                  onClick={() => updateTemplateContent('campaign_name_template', insertPlaceholder(templateSet.template_content.campaign_name_template, templateSet.template_content.secondary_grouping_field!))}>
                  +{templateSet.template_content.secondary_grouping_field} <span className="ml-1 text-xs opacity-70">(split)</span>
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Ad Group Name Template</Label>
            <Input
              value={templateSet.template_content.ad_group_name_template}
              onChange={(e) => updateTemplateContent('ad_group_name_template', e.target.value)}
              placeholder="e.g., AG - {nom_standard}"
              {...inputProps('ad_group_name_template')}
            />
            {templateSet.template_content.ad_group_name_template && sampleDataRow && (
              <div className="bg-green-50 dark:bg-green-950/30 p-2.5 rounded-md border-l-4 border-green-500 text-sm">
                <span className="text-green-700 dark:text-green-400 text-xs font-semibold uppercase tracking-wide">Preview: </span>
                <span className="text-green-900 dark:text-green-100 font-bold">{getPreviewText(templateSet.template_content.ad_group_name_template)}</span>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {selectedFields.map((ph) => (
                <Button key={ph} variant="outline" size="sm"
                  onClick={() => updateTemplateContent('ad_group_name_template', insertPlaceholder(templateSet.template_content.ad_group_name_template, ph))}>
                  +{ph}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Campaign Status</Label>
              <Select value={templateSet.template_content.campaign_status} onValueChange={(value: 'Enabled' | 'Paused') => updateTemplateContent('campaign_status', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Enabled">Enabled</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ad Group Status</Label>
              <Select value={templateSet.template_content.ad_group_status} onValueChange={(value: 'Enabled' | 'Paused') => updateTemplateContent('ad_group_status', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Enabled">Enabled</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
